const DEFAULT_TOP_BOTTOM_GUTTER = 18;

function normalizeRect(rect) {
  if (!rect) {
    return null;
  }

  const left = Number(rect.left);
  const top = Number(rect.top);
  const width = Number(rect.width);
  const height = Number(rect.height);
  if (
    !Number.isFinite(left) ||
    !Number.isFinite(top) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height)
  ) {
    return null;
  }

  return {
    left,
    top,
    width,
    height,
    right: Number.isFinite(rect.right) ? Number(rect.right) : left + width,
    bottom: Number.isFinite(rect.bottom) ? Number(rect.bottom) : top + height,
  };
}

function isPointerInsideRect(pointerCoordinates, rect) {
  if (!pointerCoordinates || !rect) {
    return false;
  }

  return (
    pointerCoordinates.x >= rect.left &&
    pointerCoordinates.x <= rect.right &&
    pointerCoordinates.y >= rect.top &&
    pointerCoordinates.y <= rect.bottom
  );
}

function findMealOption(meals, mealId, optionId) {
  const meal = (Array.isArray(meals) ? meals : []).find(
    (candidateMeal) => candidateMeal?.id === mealId,
  );
  if (!meal) {
    return null;
  }

  return (Array.isArray(meal.options) ? meal.options : []).find(
    (candidateOption) => candidateOption?.id === optionId,
  );
}

function clampInsertionIndex(index, itemCount) {
  const numericIndex = Number(index);
  if (!Number.isFinite(numericIndex)) {
    return 0;
  }

  return Math.max(0, Math.min(Math.trunc(numericIndex), itemCount));
}

export function resolveNutritionPlanFoodDragTarget({
  activeItemId = "",
  pointerCoordinates = null,
  containers = [],
  previousTarget = null,
  midpointDeadZone = 10,
  topBottomGutter = DEFAULT_TOP_BOTTOM_GUTTER,
} = {}) {
  if (!pointerCoordinates) {
    return null;
  }

  const normalizedContainers = (Array.isArray(containers) ? containers : [])
    .map((container) => {
      const rect = normalizeRect(container?.rect);
      if (!container?.mealId || !container?.optionId || !rect) {
        return null;
      }

      return {
        mealId: container.mealId,
        optionId: container.optionId,
        rect,
        items: (Array.isArray(container.items) ? container.items : [])
          .map((item, index) => {
            const itemRect = normalizeRect(item?.rect);
            if (!item?.itemId || !itemRect) {
              return null;
            }

            return {
              itemId: item.itemId,
              rect: itemRect,
              index,
            };
          })
          .filter(Boolean),
      };
    })
    .filter(Boolean);

  const targetContainer = normalizedContainers.find((container) =>
    isPointerInsideRect(pointerCoordinates, container.rect),
  );

  if (!targetContainer) {
    return null;
  }

  const visibleItems = targetContainer.items.filter(
    (item) => item.itemId !== activeItemId,
  );

  if (visibleItems.length === 0) {
    return {
      mealId: targetContainer.mealId,
      optionId: targetContainer.optionId,
      insertionIndex: 0,
    };
  }

  const firstItemRect = visibleItems[0]?.rect ?? null;
  const lastItemRect = visibleItems[visibleItems.length - 1]?.rect ?? null;
  if (!firstItemRect || !lastItemRect) {
    return {
      mealId: targetContainer.mealId,
      optionId: targetContainer.optionId,
      insertionIndex: 0,
    };
  }

  if (pointerCoordinates.y <= firstItemRect.top + topBottomGutter) {
    return {
      mealId: targetContainer.mealId,
      optionId: targetContainer.optionId,
      insertionIndex: 0,
    };
  }

  if (pointerCoordinates.y >= lastItemRect.bottom - topBottomGutter) {
    return {
      mealId: targetContainer.mealId,
      optionId: targetContainer.optionId,
      insertionIndex: visibleItems.length,
    };
  }

  for (let index = 0; index < visibleItems.length; index += 1) {
    const currentItem = visibleItems[index];
    const currentRect = currentItem.rect;
    const midpoint = currentRect.top + currentRect.height / 2;
    const lowerDeadZone = midpoint - midpointDeadZone;
    const upperDeadZone = midpoint + midpointDeadZone;

    if (pointerCoordinates.y < lowerDeadZone) {
      return {
        mealId: targetContainer.mealId,
        optionId: targetContainer.optionId,
        insertionIndex: index,
      };
    }

    if (
      pointerCoordinates.y >= lowerDeadZone &&
      pointerCoordinates.y <= upperDeadZone &&
      previousTarget?.mealId === targetContainer.mealId &&
      previousTarget?.optionId === targetContainer.optionId &&
      (previousTarget?.insertionIndex === index ||
        previousTarget?.insertionIndex === index + 1)
    ) {
      return {
        mealId: targetContainer.mealId,
        optionId: targetContainer.optionId,
        insertionIndex: previousTarget.insertionIndex,
      };
    }
  }

  return {
    mealId: targetContainer.mealId,
    optionId: targetContainer.optionId,
    insertionIndex: visibleItems.length,
  };
}

export function buildNutritionPlanFoodMovePayloadFromTarget({
  meals,
  activeItem,
  target,
} = {}) {
  const normalizedActiveItemId = String(activeItem?.itemId ?? "").trim();
  const normalizedFromMealId = String(activeItem?.mealId ?? "").trim();
  const normalizedFromOptionId = String(activeItem?.optionId ?? "").trim();
  const normalizedToMealId = String(target?.mealId ?? "").trim();
  const normalizedToOptionId = String(target?.optionId ?? "").trim();

  if (
    !normalizedActiveItemId ||
    !normalizedFromMealId ||
    !normalizedFromOptionId ||
    !normalizedToMealId ||
    !normalizedToOptionId
  ) {
    return null;
  }

  const sourceOption = findMealOption(meals, normalizedFromMealId, normalizedFromOptionId);
  const targetOption = findMealOption(meals, normalizedToMealId, normalizedToOptionId);
  if (!sourceOption || !targetOption) {
    return null;
  }

  const sourceItems = Array.isArray(sourceOption.items) ? sourceOption.items : [];
  const sourceIndex = sourceItems.findIndex((item) => item?.id === normalizedActiveItemId);
  if (sourceIndex < 0) {
    return null;
  }

  const isSameContainer =
    normalizedFromMealId === normalizedToMealId &&
    normalizedFromOptionId === normalizedToOptionId;
  const targetItems = isSameContainer
    ? sourceItems.filter((item) => item?.id !== normalizedActiveItemId)
    : Array.isArray(targetOption.items)
      ? targetOption.items
      : [];
  const insertionIndex = clampInsertionIndex(
    target?.insertionIndex,
    targetItems.length,
  );

  if (isSameContainer && insertionIndex === sourceIndex) {
    return null;
  }

  if (targetItems.length === 0) {
    return {
      fromMealId: normalizedFromMealId,
      fromOptionId: normalizedFromOptionId,
      itemId: normalizedActiveItemId,
      toMealId: normalizedToMealId,
      toOptionId: normalizedToOptionId,
      overItemId: null,
      insertAfterOverItem: false,
    };
  }

  if (insertionIndex >= targetItems.length) {
    return {
      fromMealId: normalizedFromMealId,
      fromOptionId: normalizedFromOptionId,
      itemId: normalizedActiveItemId,
      toMealId: normalizedToMealId,
      toOptionId: normalizedToOptionId,
      overItemId: targetItems[targetItems.length - 1]?.id ?? null,
      insertAfterOverItem: true,
    };
  }

  return {
    fromMealId: normalizedFromMealId,
    fromOptionId: normalizedFromOptionId,
    itemId: normalizedActiveItemId,
    toMealId: normalizedToMealId,
    toOptionId: normalizedToOptionId,
    overItemId: targetItems[insertionIndex]?.id ?? null,
    insertAfterOverItem: false,
  };
}
